import { Component, Inject, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router,ActivatedRoute } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { OnboardingService } from '../onboarding.service';
import { TranslateService } from '@ngx-translate/core';
import { Auth } from 'aws-amplify';
import { AWSLambda } from 'src/app/AWSLambda.service';
import { TUI_COUNTRIES_MASKS } from '@taiga-ui/kit/tokens';
import { TuiCountryIsoCode } from '@taiga-ui/i18n';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styles: [
    `
      .loading-mask {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(
          0,
          0,
          0,
          0.5
        ); /* semi-transparent black background */
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999; /* Ensure it's on top of other content */
      }
    `,
  ],
})

export class LoginComponent {
  showSpinner = false;
  router = inject(Router);
  onboardingService = inject(OnboardingService);
  _snackBar = inject(MatSnackBar);
  authService = inject(AuthService);
  translateService = inject(TranslateService);
  mostrarPassword = false;
  icon = 'visibility_off';
  showpasstext = 'SHOWPASS';

  countries: TuiCountryIsoCode[] = [];
  countryIsoCode = TuiCountryIsoCode.ES;

  async ngOnInit() {
    try {
      await this.loadCountries();
    } catch (error) {
      this.authService.logout();
    }
  }


  public loginForm = new FormGroup({
    username: new FormControl('', [Validators.required]),
    password: new FormControl('', [Validators.required]),
  });

    constructor(
        private lambda: AWSLambda,
        private route: ActivatedRoute,
    @Inject(TUI_COUNTRIES_MASKS) private readonly countriesMasks: Record<TuiCountryIsoCode, string>
  ) { }

  async login() {
    if (this.loginForm.invalid) return;

    const formData = this.loginForm.getRawValue();
    if (formData.username && formData.password) {
      this.showSpinner = true;
      this.onboardingService.setUsername(formData.username);
      try {
        await this.authService.login(formData.username, formData.password);

        await this.ManejoRedirection()

        const language = this.translateService.getBrowserLang()
          ? this.translateService.getBrowserLang()
          : 'en';
        const user = await Auth.currentAuthenticatedUser();

        this.translateService.use(
          user.attributes?.['locale'] ? user.attributes?.['locale'] : language
        );
      } catch (e: any) {
        this.showSpinner = false;
        this._snackBar.open(e.message, 'Close', { duration: 5000 });
        if (e.code === 'UserNotConfirmedException') {
          this.router.navigate(['/onboarding/validation'], {
            replaceUrl: true,
          });
        }
      }
    }
  }

  async ManejoRedirection(): Promise<void> {
    try {
      // PASO 1: Verificar si hay parámetro redirect
      const redirectParam = this.route.snapshot.queryParams['redirect'];

      if (redirectParam) {
        // Decodificar la URL
        const decodedUrl = decodeURIComponent(redirectParam);

        // PASO 2: Validar URL de redirect (seguridad)
        const isValidRedirect = this.isValidRedirectUrl(decodedUrl);

        if (isValidRedirect) {
          // Navegar a la URL guardada
          this.router.navigateByUrl(decodedUrl, { replaceUrl: true });
          return;
        }
        // Si no es válida, continuar con lógica de portal
      }

      // PASO 3: Sin redirect válido - usar lógica existente basada en portal
      const userPortalString = localStorage.getItem('userPortal');
      const userPortals = userPortalString
        ? userPortalString.split(',').map((e: string) => e.trim().toLowerCase())
        : [];

      if (userPortals.includes('cliente')) {
        this.router.navigate(['home'], { replaceUrl: true });
      } else if (userPortals.length === 1) {
        const portal = userPortals[0];
        if (portal === 'backoffice') {
          this.router.navigate(['/backoffice/dashboard'], { replaceUrl: true });
        } else if (portal === 'btb') {
          this.router.navigate(['/btb/dashboard'], { replaceUrl: true });
        } else {
          this.router.navigate(['home'], { replaceUrl: true });
        }
      } else if (userPortals.length === 2) {
        // If two portals exist and none is 'cliente', redirect to backoffice by default
        this.router.navigate(['/backoffice/dashboard'], { replaceUrl: true });
      } else {
        // Default fallback redirect
        this.router.navigate(['home'], { replaceUrl: true });
      }
    } catch (error) {
      console.error('Error accessing localStorage:', error);
      this.router.navigate(['home'], { replaceUrl: true });
    }
  }

  /**
   * Valida URL de redirect para prevenir ataques de open redirect
   * @param url La URL a validar
   * @returns true si la URL es segura para redireccionar
   */
  private isValidRedirectUrl(url: string): boolean {
    try {
      // Prevenir loops - no redirigir a login/onboarding
      if (url.includes('/onboarding/login') ||
          url.includes('/onboarding/register') ||
          url.includes('/onboarding/forgot')) {
        return false;
      }

      // Prevenir redirects externos - debe ser URL relativa o mismo origen
      if (url.startsWith('http://') || url.startsWith('https://')) {
        const urlObj = new URL(url);
        return urlObj.origin === window.location.origin;
      }

      // Debe empezar con / para rutas internas
      if (!url.startsWith('/')) {
        return false;
      }

      // Prevenir XSS: no permitir javascript: o data:
      if (url.toLowerCase().includes('javascript:') ||
          url.toLowerCase().includes('data:')) {
        return false;
      }

      return true;
    } catch (e) {
      // Si falla el parsing, considerar inválida
      return false;
    }
  }

   async loadCountries() {
    try {
      const res = await this.lambda.getLambdaFunction('lambda', '/get/contries', {
        queryStringParameters: { method: 'allowedToLogin' }
      });

      if (res.ok) {
        const isoList: string[] = res.countries || [];
        // Filtra los países válidos
        this.countries = isoList
          .map(code => code.toUpperCase())
          .filter(code => Object.values(TuiCountryIsoCode).includes(code as TuiCountryIsoCode))
          .map(code => code as TuiCountryIsoCode);

      } else {
        console.error('Error en la respuesta:', res.mensaje);
      }

    } catch (error) {
      console.error('Error al obtener países:', error);
    }
  }

  delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  mostrarpass() {
    this.mostrarPassword = !this.mostrarPassword; // Alternar entre true y false
    this.icon = !this.mostrarPassword ? 'visibility_off' : 'visibility'
    this.showpasstext = !this.mostrarPassword ? 'SHOWPASS' : 'HIDEPASS'
  }
}
